<template>
  <div class="page">
    <div class="wrap">
      <!-- Header centrado -->
      <div class="head" :class="{ 'has-grid': eventos.length > 0 }">
        <div class="headText">
          <div class="h1">Eventos</div>
          <div class="sub">Selecciona un evento y ejecuta una acción.</div>
        </div>

        <!-- ✅ Solo si NO hay eventos -->
        <div v-if="$canView('superadministrador') && eventos.length === 0" class="headCta">
          <button @click="showCreateModal = true" class="btn primary">
            Crear Evento
          </button>

          <CreateEventoModal
            v-if="showCreateModal"
            @close="showCreateModal = false"
            @saved="fetchEventos"
          />
        </div>
      </div>

      <!-- Grid (máx 3 en horizontal) -->
      <div class="grid" v-if="eventos.length > 0">
        <div v-for="evento in eventos" :key="evento.id_evento" class="card">
          <div class="cardHead">
            <div class="title">{{ evento.nombre }}</div>
          </div>

          <div class="cardBody">
            <div class="desc">
              {{ cardDescription(evento) }}
            </div>

            <div class="actions" :class="{ only: !hasRecintos(evento.token) && !showGestionEmpresasButton(evento) }">
              <router-link :to="`/lista-usuarios/${evento.token}`" class="btn primary full">
                Administrar usuarios
              </router-link>

              <router-link
                v-if="showGestionEmpresasButton(evento) && $canView('superadministrador', 'administradorevento')"
                :to="`/gestion-empresas-pagos/${evento.token}`"
                class="btn mt-3 soft full"
              >
                Gestión de empresas y pagos
              </router-link>
            </div>

            <!-- ✅ Recintos SOLO si existen -->
            <div v-if="hasRecintos(evento.token)" class="section">
              <div class="sectionHead">
                <div class="sectionTitle">Recintos</div>
                <div class="sectionHint">Reservas</div>
              </div>

              <div class="recList">
                <div
                  v-for="recinto in recintosPorEvento[evento.token].recintos"
                  :key="recinto.id_recinto"
                  class="recRow"
                >
                  <div class="recName">{{ recinto.nombre }}</div>

                  <div class="recBtns">
                    <router-link
                      :to="`/reservas/${recinto.id_recinto}/${recintosPorEvento[evento.token].id_evento}`"
                      class="btn soft sm"
                    >
                      Reservar
                    </router-link>

                    <button
                      v-if="$canView('superadministrador', 'administradorevento')"
                      class="btn ghost sm"
                      @click="irAReservasAdmin(recinto.id_recinto, recintosPorEvento[evento.token].id_evento)"
                    >
                      Administrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- ✅ No mostramos 'sin recintos' -->
          </div>
        </div>
      </div>

      <!-- ✅ Estado vacío (cuando NO hay eventos) -->
      <div v-else class="empty">
        <template v-if="!$canView('superadministrador')">
          No hay eventos disponibles.
        </template>
        <!-- si es superadmin, no mostramos texto (solo el botón arriba) -->
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import CreateEventoModal from "@/components/modals/eventos/CreateEventoModal.vue";
import { useRouter } from "vue-router";

export default {
  setup() {
    const toast = useToast();
    const router = useRouter();
    return { toast, router };
  },
  components: { CreateEventoModal },
  data() {
    return {
      showCreateModal: false,
      eventos: [],
      recintosPorEvento: {},
    };
  },
  mounted() {
    this.fetchEventos();
  },
  methods: {
    hasRecintos(tokenEvento) {
      return !!(this.recintosPorEvento[tokenEvento]?.recintos?.length);
    },
    hasGestionEmpresas(evento) {
      return evento?.gestion_empresas_pagos === '1' || evento?.gestion_empresas_pagos === 1 || evento?.gestion_empresas_pagos === true;
    },
    showGestionEmpresasButton(evento) {
      return this.hasGestionEmpresas(evento);
    },
    cardDescription(evento) {
      const hasRec = this.hasRecintos(evento.token);
      const hasEmp = this.showGestionEmpresasButton(evento);
      if (hasRec && hasEmp) return 'Accesos rápidos para usuarios, recintos y gestión de empresas.';
      if (hasRec) return 'Accesos rápidos para usuarios y recintos.';
      if (hasEmp) return 'Accesos rápidos para usuarios y gestión de empresas.';
      return 'Accesos rápidos para usuarios.';
    },
    irAReservasAdmin(id_recinto, id_evento) {
      this.router.push(`/admin-reservas/${id_recinto}/${id_evento}`);
    },
    fetchEventos() {
      const token = this.$store.state.user.token;
      fetch(`/api/data-eventos/${token}`)
        .then((response) => response.json())
        .then((data) => {
          this.eventos = data;
          data.forEach((evento) => this.fetchRecintos(evento.token));

          if (this.eventos.length === 1) {
            this.fetchRecintos(this.eventos[0].token, true);
          }
        })
        .catch((error) => {
          this.toast.error("Error al cargar los eventos: " + error.message);
        });
    },
    async fetchRecintos(tokenEvento, redirectIfOne = false) {
      try {
        const response = await fetch(`/api/evento/recintos-por-token/${tokenEvento}`);
        const data = await response.json();
        this.recintosPorEvento[tokenEvento] = data;

        const evento = this.eventos.find((row) => row.token === tokenEvento);
        if (redirectIfOne && data.recintos && data.recintos.length === 0 && !this.hasGestionEmpresas(evento)) {
          this.$router.push(`/lista-usuarios/${tokenEvento}`);
        }
      } catch (error) {
        this.toast.error("Error al obtener recintos: " + error.message);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.page
  padding: 16px
  background: var(--color-bg, #fff)
  color: var(--color-text, #111827)
  font-family: var(--font-family-base, Inter, system-ui)

.wrap
  max-width: 1450px
  margin: 0 auto

/* Header centrado y con más aire */
.head
  text-align: center
  display: flex
  flex-direction: column
  align-items: center
  gap: 10px
  padding: 10px 0 22px
  margin-bottom: 18px

.head.has-grid
  padding-bottom: 26px

.h1
  font-weight: 900
  font-size: 22px
  letter-spacing: -.02em

.sub
  margin-top: 6px
  font-size: 13px
  color: rgba(17,24,39,.62)

.headCta
  margin-top: 6px

/* ✅ Máximo 3 tarjetas en horizontal */
.grid
  display: grid
  gap: 16px
  grid-template-columns: 1fr

@media (min-width: 720px)
  .grid
    grid-template-columns: repeat(2, minmax(0, 1fr))

@media (min-width: 1100px)
  .grid
    grid-template-columns: repeat(3, minmax(0, 1fr))

/* Card: blanco ligeramente distinto + sombra (sin borde) */
.card
  border-radius: 14px
  background: #fbfbfd
  overflow: hidden
  box-shadow: 0 1px 2px rgba(17,24,39,.06), 0 10px 24px rgba(17,24,39,.06)
  transition: transform .15s ease, box-shadow .15s ease

.card:hover
  transform: translateY(-1px)
  box-shadow: 0 2px 4px rgba(17,24,39,.08), 0 14px 34px rgba(17,24,39,.10)

.cardHead
  padding: 14px 14px 10px

.title
  font-weight: 900
  font-size: 16px
  color: rgba(17,24,39,.92)

.cardBody
  padding: 0 14px 14px

.desc
  font-size: 13px
  color: rgba(17,24,39,.62)
  margin-bottom: 12px

.actions
  margin-bottom: 12px

.actions.only
  margin-bottom: 0

.section
  padding-top: 12px
  border-top: 1px solid rgba(17,24,39,.06)

.sectionHead
  display: flex
  align-items: baseline
  justify-content: space-between
  gap: 10px
  margin-bottom: 10px

.sectionTitle
  font-weight: 900
  font-size: 13px
  color: rgba(17,24,39,.85)

.sectionHint
  font-size: 12px
  color: rgba(17,24,39,.55)

.recList
  display: flex
  flex-direction: column
  gap: 8px

.recRow
  display: flex
  align-items: center
  justify-content: space-between
  gap: 10px
  padding: 10px
  border-radius: 12px
  background: rgba(17,24,39,.03)

.recName
  font-weight: 800
  font-size: 13px
  color: rgba(17,24,39,.84)
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
  max-width: 210px

.recBtns
  display: inline-flex
  gap: 8px
  flex-wrap: wrap
  justify-content: flex-end

/* Buttons minimal */
.btn
  display: inline-flex
  align-items: center
  justify-content: center
  padding: 10px 12px
  border-radius: 12px
  border: 1px solid rgba(17,24,39,.12)
  background: transparent
  color: rgba(17,24,39,.90)
  font-weight: 900
  font-size: 13px
  cursor: pointer
  text-decoration: none
  transition: background .15s ease, border-color .15s ease

.btn:hover
  background: rgba(17,24,39,.03)

.btn.full
  width: 100%

.btn.primary
  background: var(--color-primary, #2563EB)
  border-color: transparent
  color: #fff

.btn.primary:hover
  background: rgba(37,99,235,.92)

.btn.soft
  background: rgba(16,185,129,.12)
  border-color: rgba(16,185,129,.18)
  color: rgba(17,24,39,.92)

.btn.ghost
  background: rgba(17,24,39,.05)

.btn.sm
  padding: 8px 10px
  font-size: 12px
  border-radius: 10px

.empty
  text-align: center
  color: rgba(17,24,39,.62)
  padding: 20px 0
</style>